import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import classNames from 'classnames/bind';
import InputEmail from 'components/InputEmail/InputEmail';
import PasswordInput from 'templates/LoginPageData/components/PasswordInput/PasswordInput';
import { FORM_MODE } from 'templates/LoginPageData/LoginPageData';
import styles from 'templates/LoginPageData/LoginPageData.module.scss';
import {
  REGEXP_EMAIL,
  REGEXP_PASS_STRONG,
  getPasswordStength,
} from 'common/helpers/valdiation';
import { fetcher } from 'app/http';
import TagManager from 'react-gtm-module';
import { formError, formProgression } from 'common/helpers/Gtm';

type Props = {
  Model: any;
  PageData: any;
  onSubmit: (data: any) => void;
};

const SignInForm = ({ Model, PageData, onSubmit }: Props) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isValid, isSubmitSuccessful, isSubmitting },
    getFieldState,
  } = useForm({
    mode: 'all',
    defaultValues: {
      FormModel: {
        EmailAddress: Model?.FormModel?.EmailAddress || '',
        Password: Model?.FormModel?.Password || '',
        Mode: Model?.FormModel?.Mode,
        AuthorizeUrl: Model?.FormModel?.AuthorizeUrl,
        RedirectUrl: Model?.FormModel?.RedirectUrl,
        ProfileId: Model?.FormModel?.ProfileId,
        EmailAlreadySaved: Model?.FormModel?.EmailAlreadySaved,
      },
    },
  });

  const disabledSubmit = useMemo(() => {
    if (isSubmitSuccessful || isSubmitting) {
      return true;
    }
    if (isValid) {
      return false;
    }
    return true;
  }, [isValid, isSubmitSuccessful, isSubmitting]);

  const handleSignInForm = handleSubmit((data) => {
    onSubmit(data);
  });

  const appleIdClick = async () => {
    const response = await fetcher.get(Model?.AppleUrl);

    if (response?.data?.RedirectUrl) {
      window.location.href = response?.data?.RedirectUrl;
    }
  };
  const googleIdClick = async () => {
    const response = await fetcher.get(Model?.GoogleUrl);

    if (response?.data?.RedirectUrl) {
      window.location.href = response?.data?.RedirectUrl;
    }
  };

  const onFocusOutSignUpEmailAddress = async (e: any) => {
    formProgression(e, 'Signin', 'EmailAddress');
  };
  const onFocusOutSignUpPassword = async (e: any) => {
    formProgression(e, 'Signin', 'Password');
  };
  const passwordState = getFieldState('FormModel.Password');
  const emailAddressState = getFieldState('FormModel.EmailAddress');
  const [patternCheck, SetPatternCheck] = useState<any>(0);

  const handleInput = (e: any, callback: Function) => {
    const patterStength = getPasswordStength(e);
    if (patterStength !== 'strong') {
      SetPatternCheck(1);
    } else {
      SetPatternCheck(0);
    }
    callback(e);
  };
  useEffect(() => {
    if (passwordState.error != undefined) {
      var tagManagerArgs: any;
      tagManagerArgs = {
        dataLayer: {
          event: 'form_error',
          checkout_name: 'Registration',
          error_message: 'Password strength',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
      formError('SignIn', 'Password', 'error_Incorrect_Password');
    }
  }, [passwordState]);

  useEffect(() => {
    if (emailAddressState.error != undefined) {
      formError(
        'SignIn',
        'EmailAddress',
        'error_email_is_not_recognized_or_registered',
      );
    }
  }, [emailAddressState]);

  return (
    <form
      onSubmit={handleSignInForm}
      id="updateDateForm"
      className={styles.wrapper}
    >
      <div id="signinContainer">
        {Model.FormModel.Mode === FORM_MODE.SIGN_IN && (
          <div className="u-marginBxxsm u-marginTxxsm">
            <div className="step-wizard">
              <div className="step-wizard-container">
                <a href="javascript:history.go(-1)">
                  <img
                    src="/assets/images/icons/Chevron.svg"
                    alt="Arrow icon"
                  />
                </a>
                <p className="u-textCenter">
                  {PageData?.StepLabel}
                  <span className="u-textWeightBold"> 4 </span>
                  {PageData?.OfLabel}
                  <span className="u-textWeightBold"> 4 </span>
                </p>
                <a
                  id="cancelregistration"
                  data-modal-url={Model?.CancelRegistrationPageUrl}
                >
                  <img
                    src="/assets/images/icons/CloseIcon.svg"
                    className="u-hidden"
                  />
                </a>
              </div>
            </div>
          </div>
        )}
        <div className="auth0-lock-container">
          <div className="auth0-lock-heading">
            {PageData?.FinaliseAccountLabel}
          </div>
          <p className="form-text textleftmobile">
            {PageData?.FinaliseAccountInfoText}
          </p>
          {Model?.ShowedUserAlreadyExists && (
            <span className="password-not-recognized">
              {PageData?.UserAlreadyExistsLabel}
            </span>
          )}
          <input
            {...register('FormModel.Mode')}
            type="hidden"
            value={Model?.FormModel?.Mode}
          />
          <input
            {...register('FormModel.AuthorizeUrl')}
            type="hidden"
            value={Model?.FormModel?.AuthorizeUrl}
          />
          <input
            {...register('FormModel.RedirectUrl')}
            type="hidden"
            value={Model?.FormModel?.RedirectUrl}
          />
          <input
            {...register('FormModel.ProfileId')}
            type="hidden"
            value={Model?.FormModel?.ProfileId}
          />
          <input
            {...register('FormModel.EmailAlreadySaved')}
            type="hidden"
            value={Model?.FormModel?.EmailAlreadySaved}
          />
          <div className={styles.formField}>
            <Controller
              control={control}
              name="FormModel.EmailAddress"
              render={({
                field: { onChange, name, value },
                fieldState: { error, isDirty, invalid },
              }) => (
                <InputEmail
                  label={PageData?.EmailAddress}
                  name={name}
                  value={value}
                  error={error}
                  onChange={onChange}
                  readOnly={Model.FormModel.EmailAlreadySaved}
                  isValid={
                    (isDirty && !invalid) || Model.FormModel.EmailAlreadySaved
                  }
                  onFocusOut={(e) => onFocusOutSignUpEmailAddress(e)}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: PageData?.EmailErrorMessage,
                },
                pattern: {
                  value: REGEXP_EMAIL,
                  message: PageData?.EmailErrorMessage,
                },
              }}
            />
          </div>
          <div className={styles.formField}>
            <Controller
              control={control}
              name="FormModel.Password"
              render={({
                field: { onChange, name, value },
                fieldState: { error, isDirty, invalid },
              }) => (
                <PasswordInput
                  label={PageData?.Password}
                  name={name}
                  value={value}
                  error={error}
                  isValid={isDirty && !invalid}
                  showPassword
                  showPasswordStrength
                  onChange={(e) => handleInput(e, onChange)}
                  onFocusOut={(e) => onFocusOutSignUpPassword(e)}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: PageData?.PasswordNotSufficientMessage,
                },
                minLength: {
                  value: 8,
                  message: PageData?.PasswordLengthMessage,
                },
              }}
            />
          </div>
          <div className="auth0-lock-button-submit-panel">
            <button
              className={classNames(
                'Button u-block w-100',
                !isValid && 'is-disabled',
                patternCheck && 'is-disabled',
              )}
              id="btnLogin"
              type="submit"
              disabled={disabledSubmit}
            >
              {PageData?.CreateAccountText}
            </button>
          </div>
        </div>
        <div className="auth0-lock-social-buttons-wrapper">
          <div className="auth0-lock-social-buttons-container">
            <div className="auth0-lock-social-buttons-container__heading">
              <strong>{PageData?.ORLabel}</strong>{' '}
              {PageData?.UseSocialLoginLabel}
            </div>

            <button
              className="auth0-lock-social-button auth0-lock-social-big-button"
              data-provider="apple"
              type="button"
              onClick={appleIdClick}
            >
              <div className="auth0-lock-social-button-icon"></div>
              <div className="auth0-lock-social-button-text">
                {PageData?.ContinueWithAppleIdLabel}
              </div>
            </button>
            <button
              className="auth0-lock-social-button auth0-lock-social-big-button"
              data-provider="google-oauth2"
              type="button"
              onClick={googleIdClick}
            >
              <div className="auth0-lock-social-button-icon"></div>
              <div className="auth0-lock-social-button-text">
                {PageData?.ContinueWithGoogleLabel}
              </div>
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SignInForm;
